<script setup lang="ts">
/**
 * Bannière d'association FIXE, sur laquelle la page glisse.
 *
 * Reprend exactement le montage de la page événement (`BslugBanner`) :
 * Karim 2026-09-12, « le même style que /b/… avec l'arrondi qui passe
 * par-dessus la bannière qui ne bouge pas ».
 *
 * ⚠️ `fixed` et NON `sticky` : `html`/`body` portent `overflow-x: clip`
 * (garde anti-débordement, main.css), et `clip` neutralise `position: sticky`
 * — mesuré sur la billetterie, la bannière défilait malgré `top-0`.
 *
 * ⚠️ `z-0` et non `-z-10` : en z-index négatif elle passe derrière le fond de
 * page ET derrière son propre espaceur opaque, donc invisible.
 *
 * Les trois replis de l'ancienne bannière sont conservés à l'identique :
 * couverture → bannière de la cagnotte la mieux dotée → ambiance floutée tirée
 * du logo → dégradé de marque.
 */
defineProps<{
  name: string;
  logo: string | null;
  coverImage: string | null;
  fallbackBanner?: string | null;
  assetsUrl: string;
}>();

const { t } = useI18n();
</script>

<template>
  <div>
    <div class="fixed inset-x-0 top-0 z-0">
      <div class="w-full h-[220px] md:h-[300px] overflow-hidden">
        <HeroImage
          v-if="coverImage"
          :src="`${assetsUrl}/${coverImage}?width=1920&height=500&fit=cover&format=webp&quality=80`"
          :alt="t('association.banniereFixe.alt', { name })"
          img-class="w-full h-full object-cover"
        />
        <HeroImage
          v-else-if="fallbackBanner"
          :src="`${assetsUrl}/${fallbackBanner}?width=1920&height=500&fit=cover&format=webp&quality=80`"
          :alt="t('association.banniereFixe.alt', { name })"
          img-class="w-full h-full object-cover"
        />
        <div v-else class="w-full h-full default-cover relative">
          <template v-if="logo">
            <img
              :src="`${assetsUrl}/${logo}?width=800&height=800&fit=cover&format=webp&quality=60`"
              class="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-80"
              alt=""
            >
            <!-- Voile blanc doux : homogénéise sans noircir. -->
            <div class="absolute inset-0 bg-white/20" />
          </template>
          <div
            v-else
            class="absolute inset-0 bg-gradient-to-br from-main/15 via-gray-100 to-main2/15"
          />
        </div>
      </div>
    </div>

    <!-- Espaceur : réserve la hauteur de la bannière, qui est sortie du flux. -->
    <div class="h-[220px] md:h-[300px] pointer-events-none" />
  </div>
</template>

<style scoped>
.default-cover {
  /* Fond clair neutre : l'ambiance colorée vient du logo flouté par-dessus. */
  background: #eef1f6;
}
</style>
